/**
 * Build-time role lock (iter50, refactored iter53 for the 2-APK
 * reality — Customer APK is a union that hosts Customer + Merchant;
 * Rider APK is rider-only).
 *
 * Set ``REACT_APP_ROLE_LOCK`` in ``.env.production`` before
 * ``yarn build``. Recognised values:
 *
 *   * ``CUSTOMER_OR_MERCHANT`` — the Customer APK. Ships both the
 *     storefront and the merchant console. Login screen accepts
 *     CUSTOMER and RESTAURANT_OWNER roles.
 *   * ``RIDER``                — the Rider APK. Only the rider
 *     console is reachable.
 *   * unset                    — preview / shared dev build; every
 *     role's routes render.
 *
 * Legacy aliases (``CUSTOMER`` / ``MERCHANT``) collapse onto the
 * union so older env files keep working.
 */
const RAW = (process.env.REACT_APP_ROLE_LOCK || "").toUpperCase() || null;
// Collapse legacy single-role values onto the union — the Customer APK
// has always shipped both roles in practice.
const NORMALISED = (RAW === "CUSTOMER" || RAW === "MERCHANT") ? "CUSTOMER_OR_MERCHANT" : RAW;

export const ROLE_LOCK = NORMALISED;
export const SHOW_CUSTOMER  = !ROLE_LOCK || ROLE_LOCK === "CUSTOMER_OR_MERCHANT";
export const SHOW_MERCHANT  = !ROLE_LOCK || ROLE_LOCK === "CUSTOMER_OR_MERCHANT";
export const SHOW_RIDER     = !ROLE_LOCK || ROLE_LOCK === "RIDER";
// iter134g — Admin console now ships in the CUSTOMER_OR_MERCHANT APK
// too. The Universal Test Admin (9797979797) needs to reach the admin
// console from the mobile app via the RoleSwitcher; previously
// ``SHOW_ADMIN`` was stripped from the APK bundle so tapping "Switch
// to Admin" navigated to /admin which resolved to a 404. Rider APK
// still excludes admin — riders should never see it.
export const SHOW_ADMIN     = !ROLE_LOCK || ROLE_LOCK === "CUSTOMER_OR_MERCHANT";
export const SHOW_SUPER     = !ROLE_LOCK;
// iter181 — the branch (zone) admin console ships in the customer app too. It used to exist only in the unlocked
// web build, so "Switch to Zone admin" in the app changed the session and then landed on a missing page.
export const SHOW_ZONEADMIN = !ROLE_LOCK || ROLE_LOCK === "CUSTOMER_OR_MERCHANT";

const CONSOLE_IN_BUILD = {
    CUSTOMER: SHOW_CUSTOMER, RESTAURANT_OWNER: SHOW_MERCHANT, RIDER: SHOW_RIDER,
    TENANT_ADMIN: SHOW_ADMIN, SUPER_ADMIN: SHOW_SUPER, ZONE_ADMIN: SHOW_ZONEADMIN,
};
/** False when this build has no screens for ``role`` — the role switchers must not offer it (the switch would
 *  succeed on the server and then open a page that is not in the bundle). */
export const consoleInBuild = (role) => CONSOLE_IN_BUILD[role] !== false;

/** Per-build first-launch onboarding flavour (?app=...). The Customer APK
 *  reuses the ``customer`` flavour because the upfront permission set
 *  (Location + Notifications) is identical for both customer and
 *  merchant — camera is requested *later*, in-context. */
export const ONBOARDING_APP = ROLE_LOCK === "RIDER" ? "rider" : "customer";

/** Set of roles the backend may legitimately mint a JWT for inside
 *  this build. The login screen rejects any role outside this set so
 *  a tenant-admin / super-admin can't accidentally end up in an
 *  APK whose routes are stripped.
 *
 *  TENANT_ADMIN is included for CUSTOMER_OR_MERCHANT to match
 *  ``SHOW_ADMIN`` above (iter134g shipped the admin console in this
 *  build) — previously this list still excluded it, so a direct
 *  /admin login on mobile (e.g. AdminLayout's unauthenticated
 *  redirect) got a valid TENANT_ADMIN token from the backend but the
 *  login screen rejected it client-side with an auth error, even
 *  though switching roles via RoleSwitcher (login as CUSTOMER, then
 *  "Switch to Admin") worked fine for the same account. */
export const ALLOWED_BACKEND_ROLES = ROLE_LOCK === "RIDER"
    ? ["RIDER"]
    : ROLE_LOCK === "CUSTOMER_OR_MERCHANT"
        ? ["CUSTOMER", "RESTAURANT_OWNER", "TENANT_ADMIN", "ZONE_ADMIN"]
        : null;        // null = no lock (preview / shared dev build)

/** Default landing route after login. Customer APK lands on / and
 *  the post-verify role switch picks customer vs merchant home. */
export const LOCKED_HOME = ROLE_LOCK === "RIDER" ? "/rider" : "/";
